import * as React from 'react';
import Messages, {IMessagesRef} from '../Messages/messages';
import DraftMessages, {IDraftMessagesRef} from '../DraftMessages/draftMessages';
import './tabContainer.scss';
import {
    Accordion,
    AddIcon,
    BulletsIcon,
    Button,
    ContactGroupIcon,
    EmailIcon,
    FilesImageIcon,
    FilterIcon,
    Flex,
    FlexItem,
    Header,
    MegaphoneIcon,
    MenuButton,
    MoreIcon,
    SettingsIcon,
    ShareGenericIcon
} from '@fluentui/react-northstar';
import languageObject from "../../translations";
import translations from "../../translations";
import {AppContext, IAppContextState} from "../../services/AppContext";
import {UserPreview} from 'components/UserPreview/UserPreview';
import {withRouter, WithRouterProps} from "../../services/TelemetryService/withRouter.hook";
import {Filter} from "../Utils/Filter/Filter";
import {FilterItemsData, IFilterItem} from "../Utils/Filter/Filter.types";

export interface ITaskInfoProps extends WithRouterProps {
}

export interface ITabContainerState {
    countOfDrafts: number;
    numberOfSent: number;
    filteredAuthors: string[];
    filteredGPTs: string[];
}

class TabContainer extends React.Component<ITaskInfoProps, ITabContainerState> {
    // Define component context
    static contextType = AppContext;
    context!: IAppContextState;

    private _isMounted: boolean = false;

    private messagesRef: IMessagesRef | undefined = undefined;
    private draftMessagesRef: IDraftMessagesRef | undefined = undefined;

    constructor(props: ITaskInfoProps) {
        super(props);
        this.state = {
            countOfDrafts: 0,
            numberOfSent: 0,
            filteredAuthors: [],
            filteredGPTs: []
        }
        this.handleRefreshMessages = this.handleRefreshMessages.bind(this);
        this.handleCountOfDrafts = this.handleCountOfDrafts.bind(this);
        this.handleNumberOfSent = this.handleNumberOfSent.bind(this);
        this.handleDuplicateSentMessage = this.handleDuplicateSentMessage.bind(this);
        this.handleFilter = this.handleFilter.bind(this);
        this.getFilterAuthorsItems = this.getFilterAuthorsItems.bind(this);
        this.getAvailableGPTs = this.getAvailableGPTs.bind(this);
    }

    componentDidMount() {
        this._isMounted = true;
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    private handleRefreshMessages() {
        this.messagesRef?.refreshMessages();
    }

    private handleDuplicateSentMessage() {
        this.draftMessagesRef?.refreshMessages();
    }

    private handleCountOfDrafts(count: number) {
        if (!this._isMounted) return;
        this.setState({countOfDrafts: count});
    }

    private handleNumberOfSent(count: number) {
        if (!this._isMounted) return;
        this.setState({numberOfSent: count});
    }

    private handleFilter(data: FilterItemsData) {
        if (!this._isMounted) return;
        const selectedAuthors = Object.entries(data["author"] ?? {}).filter(([, value]) => value.isChecked).map(([key]) => key);
        const selectedGPTs = Object.entries(data["gpt"] ?? {}).filter(([, value]) => value.isChecked).map(([key]) => key);
        this.setState({
            filteredAuthors: selectedAuthors,
            filteredGPTs: selectedGPTs
        });
    }

    private getFilterAuthorsItems() {
        const messagesAuthors = this.messagesRef?.messages.map(m => m.authorName);
        const draftAuthors = this.draftMessagesRef?.messages.map(m => m.authorName);
        return Array.from(new Set(messagesAuthors?.concat(draftAuthors ?? []))).filter(Boolean).map(author => ({
            key: author,
            label: author
        }));
    }

    private getAvailableGPTs() {
        const messagesGPTs = this.messagesRef?.messages.map(m => m.gptId);
        const draftGPTs = this.draftMessagesRef?.messages.map(m => m.gptId);
        return Array.from(new Set(messagesGPTs?.concat(draftGPTs ?? []))).filter(Boolean).map(gptId => ({
            key: gptId!,
            label: this.context.gpts.list.find(gpt => gpt.id === gptId)?.fullName ?? "?"
        }));
    }

    public render(): JSX.Element {

        const panels = [
            {
                title: languageObject.translate('DraftMessagesSectionTitle') + " (" + this.state.countOfDrafts + ")",
                content: {
                    key: 'draft',
                    content: (
                        <DraftMessages
                            onRef={ref => this.draftMessagesRef = ref}
                            channelId={this.context.teams.channelId as string}
                            countOfDrafts={this.handleCountOfDrafts}
                            refreshMessages={this.handleRefreshMessages}
                            onMessagesFetch={() => this.setState({})}
                            filteredAuthors={this.state.filteredAuthors}
                            filteredGPTs={this.state.filteredGPTs}
                        />
                    )
                }
            },
            {
                title: languageObject.translate('SentMessagesSectionTitle') + " (" + this.state.numberOfSent + ")",
                content: {
                    key: 'sent',
                    content: (
                        <Messages
                            onRef={ref => this.messagesRef = ref}
                            upnContext={this.context.teams.userMail as string}
                            channelId={this.context.teams.channelId as string}
                            numberOfSent={this.handleNumberOfSent}
                            onDuplicateMessage={this.handleDuplicateSentMessage}
                            onResendMessage={this.handleRefreshMessages}
                            onMessagesFetch={() => this.setState({})}
                            filteredAuthors={this.state.filteredAuthors}
                            filteredGPTs={this.state.filteredGPTs}
                        />
                    )
                }
            }
        ];

        const filterItems: Array<IFilterItem> = [
            {
                key: "author",
                label: languageObject.translate("Author"),
                menu: {
                    items: this.getFilterAuthorsItems(),
                }
            },
            ...(this.context.tenant.enableGpt ? [{
                key: "gpt",
                label: languageObject.translate("GPT"),
                menu: {
                    items: this.getAvailableGPTs(),
                }
            }] : [])
        ]

        return (
            <Flex className="tabContainer p-20px" column fill gap="gap.small">
                <Flex gap={"gap.small"}>
                    <Flex gap={"gap.small"} vAlign={"center"}>
                        <EmailIcon styles={{color: "#782753"}} size={"large"}/>
                        <Header as={"h2"} styles={{margin: "0"}}
                                content={languageObject.translate("MessageManagement")}/>
                    </Flex>
                    <FlexItem push>
                        <Flex gap={"gap.medium"}>
                            <Button icon={<AddIcon outline/>} content={languageObject.translate("NewMessage")}
                                    onClick={this.onNewMessage} primary/>
                            <Filter
                                id={"messages-filter"}
                                items={filterItems}
                                onItemChecked={this.handleFilter}
                                enforceOpenBeforeMenu
                                renderTrigger={(active) => (
                                    <Button
                                        tinted={active}
                                        icon={<FilterIcon outline/>}
                                        content={translations.translate("Filters")}
                                    />
                                )}
                            />
                            <MenuButton
                                trigger={
                                    <Button text icon={<SettingsIcon outline/>} iconOnly styles={{width: "32px"}}/>
                                }
                                menu={[
                                    {
                                        key: "topics_page",
                                        content: languageObject.translate("Topics"),
                                        onClick: this.onShowTopics,
                                        icon: <BulletsIcon outline/>
                                    },
                                    {
                                        key: "gallery_page",
                                        content: languageObject.translate("Gallery"),
                                        onClick: this.onShowGallery,
                                        icon: (
                                            <FilesImageIcon
                                                styles={{marginBottom: "5px", marginRight: "5px"}}
                                                size={"large"}
                                                outline
                                            />
                                        )
                                    },
                                    {
                                        key: "welcome_page",
                                        content: languageObject.translate("Welcome"),
                                        onClick: this.onShowWelcome,
                                        icon: <MegaphoneIcon outline/>
                                    },
                                    {
                                        key: "mailing_lists_page",
                                        content: languageObject.translate("MailingLists"),
                                        onClick: this.onShowUsersLists,
                                        icon: <ContactGroupIcon outline/>
                                    },
                                ]}
                                on="click"
                            />
                            <MenuButton
                                on={"click"}
                                trigger={<Button icon={<MoreIcon/>} text iconOnly styles={{width: "32px"}}/>}
                                menu={[{
                                    key: "gptpro_link",
                                    content: "Admin Center - AI desk PRO",
                                    icon: <ShareGenericIcon outline/>,
                                    onClick: () => window.open("https://admin.aidesk-pro.com")
                                }]}
                            />
                            {this.context.teams.isTeamsApp ? null : <UserPreview userId={this.context.teams.userId}/>}
                        </Flex>
                    </FlexItem>
                </Flex>
                <Flex fill className={"pos-relative"}>
                    <Flex fill className="fill-absolute messageContainer">
                        <Flex.Item grow={1}>
                            <Accordion styles={{overflowX: "hidden"}} defaultActiveIndex={[0, 1]} panels={panels}/>
                        </Flex.Item>
                    </Flex>
                </Flex>
            </Flex>
        );
    }

    public onNewMessage = () => {
        this.props.navigate("/newmessage");
    }

    public onShowTopics = () => {
        this.props.navigate("/topics");
    }

    public onShowGallery = () => {
        this.props.navigate("/gallery");
    }

    public onShowWelcome = () => {
        this.props.navigate("/welcome");
    }

    public onShowUsersLists = () => {
        this.props.navigate("/users-lists");
    }
}

export default withRouter(TabContainer);